import { computed, ref, type Ref } from 'vue';

import type { TooltipPlacement, TooltipPosition } from '../types';

const TOOLTIP_OFFSET = 8; // Отступ от элемента в пикселях

export function useTooltipInteractions() {
  const isPointerInside = ref(false);
  const isFocusInside = ref(false);
  const isTouchOpen = ref(false);
  const shouldRemainVisible = computed(() => isPointerInside.value || isFocusInside.value || isTouchOpen.value);

  const setPointerInside = (value: boolean): void => {
    isPointerInside.value = value;
  };

  const setFocusInside = (value: boolean): void => {
    isFocusInside.value = value;
  };

  const toggleTouch = (): boolean => {
    isTouchOpen.value = !isTouchOpen.value;

    return isTouchOpen.value;
  };

  const dismiss = (): void => {
    isPointerInside.value = false;
    isFocusInside.value = false;
    isTouchOpen.value = false;
  };

  const escape = (): boolean => {
    const hadActiveInteraction = shouldRemainVisible.value;

    dismiss();

    return hadActiveInteraction;
  };

  return {
    isTouchOpen,
    shouldRemainVisible,
    setPointerInside,
    setFocusInside,
    toggleTouch,
    dismiss,
    escape,
  };
}

/**
 * Composable для управления позиционированием tooltip
 */
export function useTooltip(placement: Ref<TooltipPlacement>) {
  const isVisible = ref(false);
  const position = ref<TooltipPosition>({
    top: 0,
    left: 0,
    placement: 'top',
  });

  /**
   * Проверяет, помещается ли tooltip в viewport
   */
  function canFitInViewport(
    triggerRect: DOMRect,
    tooltipWidth: number,
    tooltipHeight: number,
    placement: Exclude<TooltipPlacement, 'auto'>,
  ): boolean {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    switch (placement) {
      case 'top':
        return triggerRect.top - tooltipHeight - TOOLTIP_OFFSET >= 0;
      case 'bottom':
        return triggerRect.bottom + tooltipHeight + TOOLTIP_OFFSET <= viewportHeight;
      case 'left':
        return triggerRect.left - tooltipWidth - TOOLTIP_OFFSET >= 0;
      case 'right':
        return triggerRect.right + tooltipWidth + TOOLTIP_OFFSET <= viewportWidth;
    }
  }

  /**
   * Определяет оптимальное размещение tooltip
   */
  function calculateOptimalPlacement(
    triggerRect: DOMRect,
    tooltipWidth: number,
    tooltipHeight: number,
    preferredPlacement: TooltipPlacement,
  ): Exclude<TooltipPlacement, 'auto'> {
    // Если указано конкретное размещение (не 'auto'), проверяем его
    if (preferredPlacement !== 'auto') {
      if (canFitInViewport(triggerRect, tooltipWidth, tooltipHeight, preferredPlacement)) {
        return preferredPlacement;
      }
    }

    // Пробуем размещения в порядке приоритета: top → bottom → right → left
    const placements: Array<Exclude<TooltipPlacement, 'auto'>> = ['top', 'bottom', 'right', 'left'];

    for (const placement of placements) {
      if (canFitInViewport(triggerRect, tooltipWidth, tooltipHeight, placement)) {
        return placement;
      }
    }

    // Если не помещается нигде, возвращаем 'top' как fallback
    return 'top';
  }

  /**
   * Вычисляет координаты tooltip
   */
  function calculatePosition(triggerElement: HTMLElement, tooltipElement: HTMLElement): TooltipPosition {
    const triggerRect = triggerElement.getBoundingClientRect();
    const tooltipRect = tooltipElement.getBoundingClientRect();

    const optimalPlacement = calculateOptimalPlacement(
      triggerRect,
      tooltipRect.width,
      tooltipRect.height,
      placement.value,
    );

    let top = 0;
    let left = 0;

    switch (optimalPlacement) {
      case 'top':
        top = triggerRect.top - tooltipRect.height - TOOLTIP_OFFSET;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + TOOLTIP_OFFSET;
        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        break;
      case 'left':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left - tooltipRect.width - TOOLTIP_OFFSET;
        break;
      case 'right':
        top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + TOOLTIP_OFFSET;
        break;
    }

    // Корректируем, чтобы не выходить за границы viewport по горизонтали
    const viewportWidth = window.innerWidth;
    if (left < 0) {
      left = TOOLTIP_OFFSET;
    } else if (left + tooltipRect.width > viewportWidth) {
      left = viewportWidth - tooltipRect.width - TOOLTIP_OFFSET;
    }

    // Корректируем, чтобы не выходить за границы viewport по вертикали
    const viewportHeight = window.innerHeight;
    if (top < 0) {
      top = TOOLTIP_OFFSET;
    } else if (top + tooltipRect.height > viewportHeight) {
      top = viewportHeight - tooltipRect.height - TOOLTIP_OFFSET;
    }

    return {
      top: top + window.scrollY,
      left: left + window.scrollX,
      placement: optimalPlacement,
    };
  }

  /**
   * Показывает tooltip
   */
  function show(triggerElement: HTMLElement, tooltipElement: HTMLElement) {
    isVisible.value = true;
    position.value = calculatePosition(triggerElement, tooltipElement);
  }

  /**
   * Скрывает tooltip
   */
  function hide() {
    isVisible.value = false;
  }

  return {
    isVisible,
    position,
    show,
    hide,
  };
}
